import React, { useEffect, useState } from "react";
import { Plus, School, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, Spinner, EmptyState } from "../components/common";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const ETYPE = { primaire: "Primaire", college: "College", lycee: "Lycee" };

export default function Establishments() {
  const { user } = useAuth();
  const isCentral = ["principal_admin", "finance_agent"].includes(user?.role);
  const [items, setItems] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ customer_id: "", name: "", type: "primaire", address: "", student_count: 0, director_name: "" });

  const load = () => api.get("/establishments").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/establishments", { ...form, student_count: Number(form.student_count) });
      toast.success("Etablissement ajoute");
      setOpen(false);
      setForm({ customer_id: "", name: "", type: "primaire", address: "", student_count: 0, director_name: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!items) return <Spinner />;

  return (
    <div>
      <PageHeader
        title="Etablissements"
        subtitle="Ecoles rattachees aux groupes scolaires clients."
        actions={isCentral && (
          <Button data-testid="add-establishment-btn" onClick={() => setOpen(true)} className="bg-teal-600 hover:bg-teal-700">
            <Plus className="h-4 w-4 mr-1.5" /> Ajouter
          </Button>
        )}
      />
      <Card className="p-0 overflow-hidden">
        {items.length === 0 ? (
          <EmptyState icon={School} title="Aucun etablissement" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="px-6 py-4">Etablissement</TableHead>
                <TableHead>Groupe</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Directeur</TableHead>
                <TableHead className="text-center">Eleves</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((e) => (
                <TableRow key={e.id} data-testid={`table-row-${e.id}`}>
                  <TableCell className="px-6 py-4 font-semibold text-slate-900">{e.name}</TableCell>
                  <TableCell className="text-slate-600">{e.customer_name}</TableCell>
                  <TableCell><span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">{ETYPE[e.type] || e.type}</span></TableCell>
                  <TableCell className="text-slate-600">{e.director_name || "-"}</TableCell>
                  <TableCell className="text-center font-mono font-semibold">{e.student_count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Nouvel etablissement</DialogTitle>
          <DialogDescription>Rattachez un etablissement scolaire a un groupe client.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Groupe scolaire</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                <SelectTrigger data-testid="est-customer" className="mt-1.5"><SelectValue placeholder="Selectionner" /></SelectTrigger>
                <SelectContent className="bg-white z-50">
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Nom de l'etablissement</Label><Input data-testid="est-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white z-50">
                    <SelectItem value="primaire">Primaire</SelectItem>
                    <SelectItem value="college">College</SelectItem>
                    <SelectItem value="lycee">Lycee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Nombre d'eleves</Label><Input type="number" value={form.student_count} onChange={(e) => setForm({ ...form, student_count: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Directeur</Label><Input value={form.director_name} onChange={(e) => setForm({ ...form, director_name: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="est-save" onClick={create} disabled={saving || !form.customer_id || !form.name} className="bg-teal-600 hover:bg-teal-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
